// Data links: where a field's value (a text, a choice, a picture) or a whole form comes from and goes to. The settings live in a documented
// private /FG dictionary on the widget (per field) and /FGForm in the catalog (per form); other tools ignore them. A link never holds a
// secret: it names one (`authRef`, `connectionRef`) and the Reader looks the value up in its own secure storage. TypeScript twin of
// rd_pdf::datalink.

import { PdfDocument, textString } from "./document";
import { Dict, PName, type PObj } from "./parse";
import { pdfText } from "./form";

export type LinkKind = "REST" | "WORKBOOK" | "DATABASE" | "FOLDER";
export type BodyFormat = "JSON" | "MULTIPART" | "BASE64";
export type PictureMode = "UPLOAD" | "LINK";

export const LINK_KINDS: Array<{ id: LinkKind; label: string }> = [
    { id: "REST", label: "Web API (REST)" },
    { id: "WORKBOOK", label: "Workbook / online sheet" },
    { id: "DATABASE", label: "Database (through a gateway)" },
    { id: "FOLDER", label: "File or folder" }
];

export interface DataLink {
    kind: LinkKind;
    url: string;
    method: string;
    headers: Array<[string, string]>;
    /** The name of a secret (a token); sent as `Authorization: Bearer <secret>`. */
    authRef: string;
    body: BodyFormat;
    sheet: string;
    keyColumn: string;
    keyField: string;
    table: string;
    connectionRef: string;
    /** (form field, remote column or JSON name). */
    mapping: Array<[string, string]>;
    loadOnOpen: boolean;
    saveOnSubmit: boolean;
    saveOnChange: boolean;
    picture: PictureMode;
    cache: boolean;
}

export function newLink(kind: LinkKind): DataLink {
    return { kind, url: "", method: "POST", headers: [], authRef: "", body: "JSON", sheet: "", keyColumn: "", keyField: "", table: "", connectionRef: "", mapping: [], loadOnOpen: false, saveOnSubmit: false, saveOnChange: false, picture: "UPLOAD", cache: true };
}

const isWeb = (u: string) => /^https?:\/\//i.test(u.trim());

/** The reasons the link cannot work yet: empty means it can be used. */
export function problems(l: DataLink): string[] {
    const p: string[] = [];
    const u = l.url.trim();
    if (!u) p.push(l.kind === "FOLDER" ? "Give the folder" : l.kind === "WORKBOOK" ? "Give the workbook file or sheet address" : "Give the address");
    const web = l.kind === "REST" || l.kind === "DATABASE" || u.toLowerCase().startsWith("http");
    if (web && u && !isWeb(u)) p.push("The address has to start with https://");
    if (l.kind === "DATABASE" && !l.table.trim()) p.push("Name the table");
    if (l.kind === "WORKBOOK" && !l.keyColumn.trim() && l.mapping.length) p.push("Name the key column that tells rows apart");
    return p;
}

/** Refuses a link that holds a secret in the clear (a password in the address, a token in a header or the address). Null when fine. */
export function checkNoSecrets(l: DataLink): string | null {
    const u = l.url.toLowerCase();
    const rest = u.split("://")[1];
    if (rest && (rest.split("/")[0] ?? "").includes("@")) return 'A user name and password in the address are not kept. Use a secret name in "Secret" instead';
    for (const key of ["token=", "api_key=", "apikey=", "access_key=", "secret=", "password=", "pwd=", "sig=", "key="]) {
        if (u.includes(`?${key}`) || u.includes(`&${key}`)) return 'The address carries a key or token. Keep it out of the file: store it as a named secret and put the name in "Secret"';
    }
    for (const [k, v] of l.headers) {
        const kl = k.toLowerCase(), vl = v.toLowerCase();
        if (["authorization", "x-api-key", "api-key", "x-auth-token", "cookie"].includes(kl) || vl.startsWith("bearer ") || vl.startsWith("basic ")) return `The header ${k} would put a secret in the file. Use "Secret" for the token; it is sent as an Authorization header`;
    }
    for (const r of [l.authRef, l.connectionRef]) {
        if (r.length > 64 || /\s/.test(r) || r.includes("://") || r.includes(";") || r.includes("=")) return "A secret name is a short word (letters, digits, - and _), not the secret itself";
    }
    return null;
}

const pn = (s: string) => new PName(s);

export function linkToDict(l: DataLink): Dict {
    const d = new Dict(null);
    d.set("K", pn(l.kind)); d.set("URL", pdfText(l.url)); d.set("Method", pdfText(l.method)); d.set("Auth", pdfText(l.authRef)); d.set("Body", pn(l.body));
    d.set("Sheet", pdfText(l.sheet)); d.set("KeyCol", pdfText(l.keyColumn)); d.set("KeyField", pdfText(l.keyField)); d.set("Table", pdfText(l.table)); d.set("Conn", pdfText(l.connectionRef));
    d.set("Load", l.loadOnOpen); d.set("OnSubmit", l.saveOnSubmit); d.set("OnChange", l.saveOnChange); d.set("Pic", pn(l.picture)); d.set("Cache", l.cache);
    d.set("Headers", l.headers.map(([k, v]) => pdfText(`${k}: ${v}`)));
    d.set("Map", l.mapping.flatMap(([a, b]) => [pdfText(a), pdfText(b)]));
    return d;
}

export function linkFromDict(doc: PdfDocument, d: Dict): DataLink {
    const xref = doc.xref;
    const s = (k: string) => textString(d.get(k));
    const b = (k: string) => d.get(k) === true;
    const strs = (v: PObj): string[] => (Array.isArray(v) ? v.map((x) => xref.resolve(x)).filter((x): x is string => typeof x === "string").map(textString) : []);
    const kind = d.name("K");
    const l = newLink(kind === "WORKBOOK" || kind === "DATABASE" || kind === "FOLDER" ? kind : "REST");
    l.url = s("URL"); l.method = s("Method"); l.authRef = s("Auth");
    const body = d.name("Body");
    l.body = body === "MULTIPART" || body === "BASE64" ? body : "JSON";
    l.sheet = s("Sheet"); l.keyColumn = s("KeyCol"); l.keyField = s("KeyField"); l.table = s("Table"); l.connectionRef = s("Conn");
    l.loadOnOpen = b("Load"); l.saveOnSubmit = b("OnSubmit"); l.saveOnChange = b("OnChange"); l.picture = d.name("Pic") === "LINK" ? "LINK" : "UPLOAD"; l.cache = b("Cache");
    l.headers = strs(d.get("Headers")).flatMap((t): Array<[string, string]> => { const i = t.indexOf(":"); return i < 0 ? [] : [[t.slice(0, i).trim(), t.slice(i + 1).trim()]]; });
    const m = strs(d.get("Map"));
    for (let i = 0; i + 1 < m.length; i += 2) l.mapping.push([m[i]!, m[i + 1]!]);
    return l;
}

/** The link a widget carries (its /FG /Link), or the form's (the catalog's /FGForm /Link). */
export function widgetLink(doc: PdfDocument, widget: Dict): DataLink | null {
    const l = widget.dict("FG")?.dict("Link");
    return l ? linkFromDict(doc, l) : null;
}
export function formLink(doc: PdfDocument): DataLink | null {
    const l = doc.catalog.dict("FGForm")?.dict("Link");
    return l ? linkFromDict(doc, l) : null;
}

/** Puts (or with null removes) a link on a widget dictionary. */
export function setWidgetLink(widget: Dict, link: DataLink | null): void {
    const fg = new Dict(null);
    const old = widget.dict("FG");
    if (old) for (const k of old.keys()) fg.set(k, old.raw(k) as PObj);
    if (link) fg.set("Link", linkToDict(link)); else fg.map.delete("Link");
    if (fg.keys().length) widget.set("FG", fg); else widget.map.delete("FG");
}
export function setFormLink(catalog: Dict, link: DataLink | null): void {
    const fg = new Dict(null);
    const old = catalog.dict("FGForm");
    if (old) for (const k of old.keys()) fg.set(k, old.raw(k) as PObj);
    if (link) fg.set("Link", linkToDict(link)); else fg.map.delete("Link");
    if (fg.keys().length) catalog.set("FGForm", fg); else catalog.map.delete("FGForm");
}

/** The pairs a save sends, with the link's mapping applied (form field → remote name). Fill identity travels with every row. */
export function mappedRow(l: DataLink, pairs: Array<[string, string]>): Array<[string, string]> {
    if (!l.mapping.length) return pairs;
    const out: Array<[string, string]> = [];
    for (const [field, remote] of l.mapping) { const p = pairs.find(([k]) => k === field); if (p) out.push([remote, p[1]]); }
    for (const [k, v] of pairs) if (k.startsWith("fg_fill_") && !out.some(([o]) => o === k)) out.push([k, v]);
    return out;
}

const text = (v: unknown): string => (typeof v === "string" ? v : JSON.stringify(v));

/** What comes back mapped the other way (remote name → form field) from a JSON object. A remote name may be a path: `person.name`. */
export function unmapValues(l: DataLink, obj: Record<string, unknown>): Array<[string, string]> {
    if (!l.mapping.length) return Object.entries(obj).map(([k, v]): [string, string] => [k, text(v)]);
    const out: Array<[string, string]> = [];
    for (const [field, remote] of l.mapping) {
        let cur: unknown = obj;
        for (const part of remote.split(".")) cur = cur && typeof cur === "object" ? (cur as Record<string, unknown>)[part] : undefined;
        if (cur !== undefined) out.push([field, text(cur)]);
    }
    return out;
}

// ---- CSV (a Workbook export, or the CSV a sheet's API returns) --------------------------------------------------------------------------

export function parseCsv(src: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [], cell = "", quoted = false;
    const t = src.replace(/^﻿/, "");
    for (let i = 0; i < t.length; i++) {
        const c = t[i]!;
        if (c === '"') { if (quoted && t[i + 1] === '"') { cell += '"'; i++; } else quoted = !quoted; }
        else if (c === "," && !quoted) { row.push(cell); cell = ""; }
        else if (c === "\r" && !quoted) { /* dropped */ }
        else if (c === "\n" && !quoted) { row.push(cell); rows.push(row); row = []; cell = ""; }
        else cell += c;
    }
    if (cell || row.length) { row.push(cell); rows.push(row); }
    return rows;
}

export function writeCsv(rows: string[][]): string {
    const q = (c: string) => (/[,"\n\r]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c);
    return rows.map((r) => r.map(q).join(",")).join("\r\n") + "\r\n";
}

/** The row of `rows` (first row = header) whose `keyColumn` equals `key`, as (column, value) pairs. */
export function csvLookup(rows: string[][], keyColumn: string, key: string): Array<[string, string]> {
    const head = rows[0];
    if (!head) throw new Error("The sheet is empty");
    const k = head.indexOf(keyColumn);
    if (k < 0) throw new Error(`The sheet has no column named "${keyColumn}"`);
    const r = rows.slice(1).find((x) => x[k] === key);
    if (!r) throw new Error(`No row has ${keyColumn} = ${key}`);
    return head.map((h, i): [string, string] => [h, r[i] ?? ""]);
}

/** Puts `row` in the table: replaces the row whose key column equals `key`, else appends; new columns are added. */
export function csvUpsert(rows: string[][], keyColumn: string, key: string, row: Array<[string, string]>): string[][] {
    const out = rows.length ? rows.map((r) => [...r]) : [[]];
    for (const [name] of row) if (!out[0]!.includes(name)) out[0]!.push(name);
    const head = out[0]!;
    const cells = head.map((h) => row.find(([k]) => k === h)?.[1] ?? "");
    const kcol = head.indexOf(keyColumn);
    const at = kcol >= 0 && key ? out.slice(1).findIndex((r) => r[kcol] === key) + 1 : 0;
    if (at > 0) {
        const merged = [...out[at]!];
        while (merged.length < head.length) merged.push("");
        head.forEach((h, c) => { if (row.some(([k]) => k === h)) merged[c] = cells[c]!; });
        out[at] = merged;
    } else out.push(cells);
    for (let i = 1; i < out.length; i++) while (out[i]!.length < head.length) out[i]!.push("");
    return out;
}

// ---- running a link: requests built without any I/O, so they can be checked ------------------------------------------------------

export interface LinkRequest { method: string; url: string; headers: Array<[string, string]>; body?: string }

/** The headers of a request: the link's own, and the secret as a bearer token. `secret(name)` looks it up in the secure storage. */
export function requestHeaders(l: DataLink, secret: (name: string) => string | null): Array<[string, string]> {
    const h = [...l.headers];
    const name = l.authRef || l.connectionRef;
    if (name) {
        const v = secret(name);
        if (v === null) throw new Error(`The secret "${name}" is not set in this browser. Set it in Properties > Data link > Set secret`);
        h.push(["Authorization", `Bearer ${v}`]);
    }
    return h;
}

const pct = (s: string) => encodeURIComponent(s).replace(/[!'()*]/g, (c) => "%" + c.charCodeAt(0).toString(16).toUpperCase());

/** The request that loads a row (or a whole form) from the source. `key` is the value of the key field. */
export function loadRequest(l: DataLink, key: string, secret: (n: string) => string | null): LinkRequest {
    const headers = requestHeaders(l, secret);
    let url = l.url.trim();
    if (l.kind === "DATABASE") url = `${url.replace(/\/$/, "")}/${pct(l.table)}${l.keyColumn && key ? `?${pct(l.keyColumn)}=eq.${pct(key)}` : ""}`;
    else if (l.kind === "REST" && key) url = url.includes("{key}") ? url.replace("{key}", pct(key)) : `${url}${url.includes("?") ? "&" : "?"}${pct(l.keyColumn || "id")}=${pct(key)}`;
    return { method: "GET", url, headers: [["Accept", "application/json"], ...headers] };
}

/** The request that saves the answers. */
export function saveRequest(l: DataLink, pairs: Array<[string, string]>, secret: (n: string) => string | null): LinkRequest {
    const headers = requestHeaders(l, secret);
    const row = mappedRow(l, pairs);
    const obj = Object.fromEntries(row);
    const url = l.kind === "DATABASE" ? `${l.url.trim().replace(/\/$/, "")}/${pct(l.table)}` : l.url.trim();
    return { method: l.method || "POST", url, headers: [["Content-Type", "application/json"], ...headers], body: JSON.stringify(l.kind === "DATABASE" ? { table: l.table, row: obj } : obj) };
}

/** Test connection: a GET (or HEAD) that must answer; no data is read into the form. */
export function testRequest(l: DataLink, secret: (n: string) => string | null): LinkRequest {
    return { method: "GET", url: l.url.trim(), headers: [["Accept", "application/json, text/csv, */*"], ...requestHeaders(l, secret)] };
}

/** A secret name is a short word: letters, digits, `-`, `_` and `.`. */
export const validSecretName = (n: string): boolean => /^[A-Za-z0-9._-]{1,64}$/.test(n);
